import {
    capitalize,
    FCX,
    getFormattedThousand, ID,
    includesInLC,
    markMatch,
    startsFromInLC,
    TextInput, Tooltip
} from "@datassential/platform-ui-lib";
import Page from "@containers/Page/Page";
import PageContent from "@containers/PageContent/PageContent";
import './MacsList.scss';
import { AiOutlineInfoCircle, AiOutlineSearch } from "react-icons/ai"
import { IMacItem, useMacsList } from "@apiHooks/useMacsList";
import Table from "@components/new/Table/Table";
import React, { ComponentProps, useCallback, useMemo, useState } from "react";
import { iTableConfigItem } from "@components/new/Table/Table.helpers";
import useTableSort from "@components/new/Table/useTableSort";
import { GoPackage } from "react-icons/go";
import { BsDownload } from 'react-icons/bs';
import { IoShareSocial } from "react-icons/io5";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { addFileToPackage } from "@actions/packages.actions";
import { Link } from "react-router-dom";
import { routeNames } from "@core/routeNames";
import { sanitizeUrl } from "@helpers";
import http from "@core/fetch";
import SmallLoader from "@components/SmallLoader/SmallLoader";
import cn from "classnames";
import { setSharePopupState } from "@actions/documents.actions";
import { shareDataSelector } from "@selectors/documents.selectors";
import SharePopup from "@pages/_shared/SharePopup/SharePopup";

function getMacItemSortingScore(item: IMacItem, query: string): number {
    if (startsFromInLC(item.title, query) || startsFromInLC(item.description, query)) {
        return 100;
    } else if (includesInLC(item.title, query) || includesInLC(item.description, query)) {
        return 50;
    } else return 1;
}

const RowLink: FCX<{
    to?: ComponentProps<Link>['to'];
}> = (props) => {
    return (
        /* @ts-ignore */
        <Link
            {...props}
            to={props.to ?? ''}
        />
    );
};

const MacsList: FCX = () => {
    const dispatch = useDispatch();
    const isSharePopupOpen = useSelector(shareDataSelector);
    const [downloadingItems, setDownloadingItems] = useState<ID[]>([]);
    const [query, setQuery] = useState<string>('');
    const {
        data: rawData,
        isLoading,
    } = useMacsList();

    const {
        sortedData,
        sortingDirection,
        sortingField,
        getColumnIconNoDefault,
        handleSort,
    } = useTableSort(
        rawData,
        'dateSort',
        'desc',
        {
            title: 'asc',
            description: 'asc',
            itemsCount: 'desc',
            dateSort: 'desc',
        }
    );

    const data = useMemo(
        () => {
            if (query.trim() !== '') {
                const result = sortedData.filter((item) => {
                    return includesInLC(item.title, query) || includesInLC(item.description, query);
                });

                result.sort((a, b) => {
                    const aScore = getMacItemSortingScore(a, query);
                    const bScore = getMacItemSortingScore(b, query);

                    return bScore - aScore;
                });

                return result;
            }

            return [...sortedData];
        },
        [sortedData, query],
    );

    const share = useCallback(
        (item: IMacItem) => {
            dispatch(setSharePopupState({
                id: item.id,
                documentType: item.documentType,
                shareLinkString: item.shareLink,
                publicationFamily: item.publicationFamily,
                title: item.title,
                popupType: 'allDocuments'
            }));
        },
        [dispatch]
    );

    const addToPackage = useCallback((id: ID) => {
        toast.success('Item has been added to package');
        dispatch(addFileToPackage({
            itemId: id,
            itemDocumentType: 'mac',
        }));
    }, [dispatch]);

    const downloadFile = useCallback(
        async (url: string, itemId: ID) => {
            setDownloadingItems((prev) => [...prev, itemId]);
            try {
                await http.downloadFileXHRFromUrl('get', url);
                toast.success('File has been downloaded');
            } catch (e) {
                toast.error('Error while downloading file');
            }
            setDownloadingItems((prev) => prev.filter((id) => id !== itemId));
        },
        []
    );

    const tableConfig = useMemo(
        (): iTableConfigItem[] => {
            return [
                {
                    title: 'Country',
                    icon: getColumnIconNoDefault('countryCodeStr', sortingField, sortingDirection),
                    handleHeaderClick: handleSort('countryCodeStr'),
                    styles: {
                        cell: {
                            flex: '0 0 80px',
                            paddingLeft: 0,
                            justifyContent: 'center',
                        },
                    },
                    Value: ({ index }) => <>{data[index]?.countryCodeStr}</>,
                },
                {
                    title: 'Name',
                    icon: getColumnIconNoDefault('title', sortingField, sortingDirection),
                    handleHeaderClick: handleSort('title'),
                    styles: {
                        cell: {
                            flex: '2',
                        },
                    },
                    getHtmlAttributes: (index, isHeader): Record<string, string> => {
                        if (!isHeader) {
                            return {
                                'data-tooltip-id': `macs-list-table-item-thumbnail-${data[index]?.id}`,
                            };
                        }

                        return {};
                    },
                    Value: ({ index }) => (
                        <div
                            dangerouslySetInnerHTML={{
                                __html: query.trim() === ''
                                    ? data[index]?.title
                                    : markMatch(data[index]?.title, query.trim(), true)
                            }}
                        />
                    ),
                },
                {
                    title: 'Description',
                    icon: getColumnIconNoDefault('description', sortingField, sortingDirection),
                    handleHeaderClick: handleSort('description'),
                    Value: ({ index }) => (
                        <div
                            dangerouslySetInnerHTML={{
                                __html: query.trim() === ''
                                    ? data[index]?.description
                                    : markMatch(data[index]?.description, query.trim(), true)
                            }}
                        />
                    ),
                    styles: {
                        cell: {
                            flex: '3',
                        },
                    },
                },
                {
                    title: 'Items Count',
                    Value: ({ index }) => <>{getFormattedThousand(data[index]?.itemsCount)}</>,
                    icon: getColumnIconNoDefault('itemsCount', sortingField, sortingDirection),
                    handleHeaderClick: handleSort('itemsCount'),
                    styles: {
                        cell: {
                            flex: '0 0 80px',
                            justifyContent: 'center',
                            textAlign: 'center',
                        },
                    },
                },
                {
                    title: 'Publish Date',
                    Value: ({ index }) => <>{data[index]?.dateStr}</>,
                    handleHeaderClick: handleSort('dateSort'),
                    icon: getColumnIconNoDefault('dateSort', sortingField, sortingDirection),
                    styles: {
                        cell: {
                            flex: '0 0 100px',
                            justifyContent: 'center',
                            textAlign: 'center',
                        },
                    },
                },
                {
                    title: 'Actions',
                    Value: ({ index }) => {
                        const item = data[index];
                        return (
                            <div
                                className="MacsList__actions"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                }}
                            >
                                <div
                                    className={cn(
                                        "MacsList__action",
                                        downloadingItems.includes(item.id) && 'is-disabled'
                                    )}
                                    title="Download"
                                    data-testid={`buttonAllMacsDownload-${index}`}
                                >
                                    {downloadingItems.includes(item.id) ? (
                                        <SmallLoader className="MacsList__action-loader"/>
                                    ) : (<>
                                        <BsDownload data-tooltip-id={`macs-list-table-item-download-${item?.id}`}/>
                                    </>)}
                                </div>
                                <GoPackage
                                    className="MacsList__action is-clickable"
                                    onClick={() => {
                                        addToPackage(item?.id);
                                    }}
                                    title="Add to package"
                                    data-testid={`buttonAllMacsAddToPackage-${index}`}
                                />
                                <IoShareSocial
                                    title="Share"
                                    className="MacsList__action is-clickable"
                                    onClick={() => share(item)}
                                    data-testid={`buttonAllMacsShare-${index}`}
                                />
                            </div>
                        );
                    },
                    styles: {
                        cell: {
                            flex: '0 0 150px',
                            paddingLeft: 0,
                            justifyContent: 'center',
                        },
                    },
                },
            ];
        },
        [data,
            getColumnIconNoDefault,
            handleSort,
            sortingField,
            sortingDirection,
            query,
            addToPackage,
            downloadingItems,
            share
        ],
    );

    return (
        /* @ts-ignore */
        <Page className="MacsList">
            <PageContent>
                <div className="MacsList__header">
                    <div className="MacsList__wrapper is-flex-row">
                        <div>
                            <span data-testid="textAllMacsTitle">
                                MAC - Menu Adoption Cycle
                            </span>
                            <a
                                href={`https://ds-projects-shared.s3.us-west-2.amazonaws.com/snap/MAC_DIAGRAM.pdf`}
                                target="_blank"
                                title="Download information"
                                rel="noreferrer"
                                className="MacsList__header-icon"
                                data-testid="buttonAllMacsDownloadInfo"
                            >
                                <AiOutlineInfoCircle/>
                            </a>
                        </div>
                    </div>
                </div>
                <div className="MacsList__wrapper">
                    <div className="MacsList__description">
                        <div data-testid="textAllMacsDescription">
                            The MAC, Menu Adoption Cycle, tracks foods, beverages, and ingredients along their
                            <br/>
                            trend curve across 4-stages: Inception, Adoption, Proliferation, and Ubiquity.
                        </div>
                        <div className="MacsList__search-box">
                            <TextInput
                                value={query}
                                setValue={setQuery}
                                placeholder="Search for MAC"
                                className="MacsList__search"
                                modifiers={['no-rem']}
                                testId="inputAllMacsSearch"
                            />
                            <AiOutlineSearch className="MacsList__search-icon"/>
                        </div>
                    </div>
                    <div className="MacsList__table">
                        <Table
                            isCopyButtonHidden
                            isLoading={isLoading}
                            config={tableConfig}
                            rowsCount={data.length}
                            theme="light"
                            RowComponent={RowLink}
                            testId={"tableAllMacs"}
                            getRowProps={(index) => ({
                                to: {
                                    pathname: routeNames.viewDocument,
                                    search: `?id=${data[index]?.id}&doctype=${data[index]?.documentType}`
                                }
                            })}
                        />
                        {data.map((item, index) => (<>
                            <Tooltip
                                key={item.id}
                                id={`macs-list-table-item-thumbnail-${item.id}`}
                                place="right"
                                style={{
                                    fontSize: 0,
                                    lineHeight: 0,
                                }}
                                delayShow={200}
                            >
                                <div
                                    className="MacsList__thumbnail"
                                    data-testid={`buttonAllMacsThumbnail-${index}`}
                                >
                                    <img src={item.thumbnailUrl} alt="thumbnail"/>
                                </div>
                            </Tooltip>
                            <Tooltip
                                id={`macs-list-table-item-download-${item.id}`}
                                place="left"
                                clickable
                            >
                                <div className="MacsList__download-links">
                                    {item.downloadLinks.map((link) => (
                                        <div
                                            key={link.fileExtension}
                                            onClick={() => downloadFile(
                                                sanitizeUrl(`${process.env.REACT_APP_API}/${link.url}`),
                                                item.id,
                                            )}
                                            className="MacsList__download-link"
                                            data-testid={`buttonAllMacsDownloadLink${capitalize(link.fileExtension)}-${index}`}
                                        >
                                            {link.fileExtension}
                                        </div>
                                    ))}
                                </div>
                            </Tooltip>
                        </>))}
                    </div>
                    {/* @ts-ignore */}
                    {isSharePopupOpen && <SharePopup/>}
                </div>
            </PageContent>
        </Page>
    );
};

export default MacsList;